import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { User, LogOut, Home, BookOpen, Camera } from 'lucide-react';

export const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-50 glass border-b border-stone-200" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2"
            data-testid="logo-link"
          >
            <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center shadow-lg">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-black font-['Tajawal'] gradient-primary-text">
              رشيد GPT
            </span>
          </Link>

          {/* Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <Link 
              to="/home" 
              className="text-stone-600 hover:text-orange-500 transition-colors font-medium"
              data-testid="nav-home"
            >
              <span className="flex items-center gap-2">
                <Home className="w-4 h-4" />
                الرئيسية
              </span>
            </Link>
            <Link 
              to="/grades" 
              className="text-stone-600 hover:text-orange-500 transition-colors font-medium"
              data-testid="nav-grades"
            >
              <span className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                الصفوف
              </span>
            </Link>
            <Link 
              to="/solve-image" 
              className="text-stone-600 hover:text-orange-500 transition-colors font-medium"
              data-testid="nav-solve-image"
            >
              <span className="flex items-center gap-2">
                <Camera className="w-4 h-4" />
                حل سؤال
              </span>
            </Link>
          </nav>

          {/* User Menu */}
          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="flex items-center gap-2 hover:bg-orange-50"
                  data-testid="user-menu-trigger"
                >
                  <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <span className="hidden sm:inline text-stone-700 font-medium">
                    {user?.name}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <div className="px-3 py-2 border-b border-stone-100">
                  <p className="font-semibold text-stone-900">{user?.name}</p>
                  <p className="text-sm text-stone-500">{user?.role === 'student' ? 'طالب' : 'ولي أمر'}</p>
                </div>
                <DropdownMenuItem asChild className="md:hidden">
                  <Link to="/home" className="flex items-center gap-2 cursor-pointer">
                    <Home className="w-4 h-4" />
                    الرئيسية
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="md:hidden">
                  <Link to="/grades" className="flex items-center gap-2 cursor-pointer">
                    <BookOpen className="w-4 h-4" />
                    الصفوف
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="md:hidden">
                  <Link to="/solve-image" className="flex items-center gap-2 cursor-pointer">
                    <Camera className="w-4 h-4" />
                    حل سؤال
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="md:hidden" />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-red-600 cursor-pointer"
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4 me-2" />
                  تسجيل الخروج
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
